import AddIcon from "@mui/icons-material/Add";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { alpha, Card, CardHeader, Button, Divider, Box, Typography, Tooltip, IconButton, useTheme, Stack, FormGroup, FormControlLabel, Switch } from "@mui/material";
import { useDeleteWithConfirmController, useListController, ListContextProvider } from "ra-core";
import { FC, useState, useRef } from "react";
import { FunctionField } from "@/components/field";
import ReadonlyField from "@/components/input/ReadonlyField";
import { Datagrid } from "@/components/list/datagrid";
import Scrollbar from "@/components/Scrollbar";
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import { RecPatch } from "@/dataProvider/tools";
import { RegisterCodeResponse } from "@/gen/api/user";
import { autoCopyTextFieldProps } from "@/utils/clipboard";
import { useUserTimeFormat } from "@/utils/userTimeFormat";
import Confirm from "@/components/widgets/Confirm";
import CreateRegisterCode from "../clients/CreateRegisterCode";



const RegistrationCard : FC<{resource:string, headerTitle:string}> = ({
  resource, headerTitle
}) => {
  const [states, setStates] = useState({
    openCreate: false,
    recordToBeVoided: null as RegisterCodeResponse|null,
    copied: false,
    includeExpired: false,
  })

  const listCtrl = useListController({ resource, basePath: '', filter: { includeExpired: states.includeExpired } })
  const { refetch } = listCtrl

  const format = useUserTimeFormat()
  
  const delHandlers = useDefaultSavedHandlers('Invalidate Registration Code');
  delHandlers.onSuccess.merge(refetch)
  
  const tempDelRec = useRef(null as RegisterCodeResponse|null)
  const delRec = states.recordToBeVoided ?? tempDelRec.current

  const {
    open: openConfirm,
    loading,
    handleDelete,
    handleDialogClose,
    handleDialogOpen,
  } = useDeleteWithConfirmController({ 
    resource,
    record: states.recordToBeVoided as any,
    ...delHandlers,
  })

  const theme = useTheme()
  const now = new Date()
  const rowStyles = (d:RegisterCodeResponse&RecPatch) => {
    const expired = d.voided || new Date(d.expiration!) < now
    return expired ? { backgroundColor: alpha(theme.palette.error.light, 0.05) } : {}
  }
  return (
    <>
      <ListContextProvider value={listCtrl}>
        <Card>
          <CardHeader title={headerTitle || "Registration"}
            action={
              <Stack direction="row" spacing={2}>
                <FormGroup>
                  <FormControlLabel sx={{ ml: 'auto' }} 
                    label="Show Used Code" 
                    control={
                      <Switch checked={states.includeExpired} 
                        onChange={(e, checked) => setStates(v => ({...v, includeExpired: checked}))} />
                    } 
                  />
                </FormGroup>
                <Button color="primary" variant="contained" size="small"
                  startIcon={<AddIcon />}
                  onClick={() => { setStates(v => ({...v, openCreate: true})) }}
                >
                  Add Registration Code
                </Button>
              </Stack>
            }
          />
          <Divider />
          <Scrollbar options={{ suppressScrollY: true }}>
            <Box sx={{ minWidth: 1150 }}>
              <Datagrid
                hasBulkActions={false} 
                sx={{ 
                  '& th' : { py: 2 },
                  '& td' : { py: 2 },
                }}
                rowStyle={rowStyles}
              >
                <FunctionField source="code" label="Registration Code" sortable={false} 
                  render={(d?:RegisterCodeResponse&RecPatch) => (
                    <Box
                      sx={{
                        width: 350, height: 40,
                        'p.masked': { visibility: 'visible', display: 'block', lineHeight: '40px', ml: 2, },
                        'div.box': { visibility: 'hidden', display: 'none' },
                        '&:hover': {
                          'p.masked': { visibility: 'hidden', display: 'none' },
                          'div.box': { visibility: 'visible', display: 'block' },
                        }
                      }}
                    >
                      <Typography variant="h6" component="p" className="masked"><code>************</code></Typography>
                      <Tooltip title={states.copied ? 'Copied' : 'Click to Copy'}>
                        <Box className="box">
                          <ReadonlyField 
                            id={`code-generated${d?.codeId}`}
                            size="small"
                            value={d!.code!}
                            {...autoCopyTextFieldProps(
                              () => setStates(v => ({...v, copied: true})), 
                              () => setTimeout(() => setStates(v => ({...v, copied: false})), 500)
                            )}
                            {...{inputProps: {tabIndex: 0}}}
                          />
                        </Box>
                      </Tooltip>
                    </Box>
                  )}
                />
                <FunctionField source="creatorId" label="Creator" sortable={false} 
                  render={(d?:RegisterCodeResponse&RecPatch) => (
                    <Tooltip title={d?.creatorUserame ?? ''}>
                      <Box>
                        <Typography variant="body2">
                          {d?.creatorFirstName} {d?.creatorLastName}
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}
                />
                <FunctionField source="createTime" label="Validity Period" sortable={false} 
                  render={(d?:RegisterCodeResponse&RecPatch) => (
                    <Box
                      sx={{
                        my: -0.5,
                        'p.MuiTypography-caption > span.h': { display: 'inline-block', width: 36},
                        'p.MuiTypography-caption > span.until': { color: theme => theme.palette.primary.main },
                        'p.MuiTypography-caption > span.until.voided': { color: theme => theme.palette.error.light },
                      }}
                    >
                      <Typography variant="caption" component="p">
                        <span className="h">From</span><span>{format(d?.createTime!, 'Pp')}</span>
                      </Typography>
                      <Typography variant="caption" component="p">
                        <span className="h">Until</span> 
                        <span className={`until ${d?.voided || new Date(d!.expiration!) < now ? 'voided' : ''}`}>
                          {format(d?.expiration!, 'Pp')}
                        </span>
                      </Typography>
                    </Box>
                  )}
                />
                <FunctionField label="Action" sortable={false} textAlign="center" 
                  render={(d?:RegisterCodeResponse&RecPatch) => (
                    d && !d.voided && new Date(d.expiration!) > now &&
                    <>
                      <Tooltip title="Delete">
                        <IconButton
                          sx={{
                            color: 'text.primary' as any,
                            '&:hover': { backgroundColor: 'action.hover' },
                          }}
                          onClick={(e:any) => {
                            setStates(v => ({...v, recordToBeVoided: d}))
                            tempDelRec.current = null
                            handleDialogOpen(e)
                          }}
                        >
                          <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                />
              </Datagrid>
            </Box>
          </Scrollbar>
          {//@ts-ignore 
          <DefaultPagination {...listCtrl} />}
        </Card>
        {states.openCreate && 
          <CreateRegisterCode resource={resource}
            fixedAllowUsage={1}
            open={states.openCreate} 
            setOpen={(x:boolean) => setStates(v => ({...v, openCreate: x})) } 
          />}
      </ListContextProvider>
      <Confirm
        isOpen={openConfirm}
        title={"Declare Registration Code Voided"}
        loading={loading}
        onConfirm={handleDelete}
        onClose={(e:any) => {
          handleDialogClose(e); 
          setStates(v => {
            const v1 = ({...v, recordToBeVoided:null})
            tempDelRec.current = v.recordToBeVoided
            return v1
          })
          setTimeout(() => { tempDelRec.current = null }, 5000)
        }}
        confirm="Confirm"
        cancel="Cancel"
        content={(
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 1 }}
              >
                Please confirm whether to invalidate this
                "{delRec?.code}"
                Registration Code.
              </Typography>
            </Box>
          </Box>
        )}
      />
    </>
  )
}

export default RegistrationCard;
